import React, { ComponentType, useState, useRef } from 'react';
import ReactTooltip from 'react-tooltip';
import styled from '@emotion/styled';

import {
  Chip as CrystalChip,
  Tooltip as CrystalTooltip,
  Flex,
  Markdown,
  Tag,
  useId,
} from '@applyboard/crystal-ui';
import { Chip, Box, IconButton, Tooltip } from '@applyboard/ui-components';
import {
  EditOutlineIcon,
  FormatListBulletedOutlineIcon,
  InfoOutlineIcon,
  LinkOutlineIcon,
} from '@applyboard/ui-icons';
import {
  copyToClipboard,
  formatCurrency,
  useEventTracker,
  IEvent,
  raiseInfoMessage,
} from '@applyboard/ui-utils';

import {
  STUDENT_RECORDS_ICON as StudentRecordIcon,
  APPLYPROOF_ICON as ApplyproofIcon,
} from '~/util/cdnAssets';
import useInitialProps from '~/hooks/useInitialProps';
import RequirementCardBottomBar from '../RequirementCardBottomBar';
import AdditionalAttachments from './AdditionalAttachments';
import ExternalLinks from './ExternalLinks';
import SeeMore from '~/components/SeeMore';
import RawHtml from '~/components/RawHtml';
import RequirementStatus from '../RequirementStatus';
import RequirementFlags from './RequirementFlags';
import { FulfillRequirementButton } from './FulfillRequirementButton';
import {
  RequirementConditionTitle,
  RequirementDetails,
  VerticalAlign,
  TypeIcons,
  PaymentRequirementPromotion,
  RequirementCardContentWrapper,
} from './style';
import { FulfillmentMessageCode, strings } from './const';
import { IIconType } from './RequirementFlags/types';

import { FlywireOptions } from './Flywire/FlywireOptions';

import DueDate from './DueDate';
import { isInternalRequirement } from '~/data/requirements';
import { loadBaseApplication } from '../../enhancer';

const FulfillmentTooltip = styled(ReactTooltip)`
  max-width: 300px;
`;

interface Requirement {
  application_additional_attachments: any[]; // eslint-disable-line @typescript-eslint/no-explicit-any
  application_external_links: any[]; // eslint-disable-line @typescript-eslint/no-explicit-any
  application_stage: string;
  applyproof_compatible: boolean;
  classification: string;
  completed_by: string;
  currency_amount: {
    amount_cents: number;
    currency: string;
  };
  early_access_description: string;
  early_access_given: boolean;
  file_types: string[];
  final_price_cents: number;
  flywire: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  id: string;
  is_early_access: boolean;
  official: boolean;
  once_per_student: boolean;
  required_state: string;
  requirement_category: string;
  requirement_template_id: number;
  requirement_template_type:
    | 'document'
    | 'receipt'
    | 'questions'
    | 'payment'
    | 'disclaimer'
    | 'hybrid';
  status: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  title: string;
}

type FulfillmentMessageProps = {
  type: 'error' | 'info';
  code: FulfillmentMessageCode;
  mb: number;
};

const statusUpdateEventType = {
  status_not_approved: 'Not Approved',
  status_completed: 'Completed',
  status_reviewing: 'Reviewing',
  status_missing: 'Missing',
  status_no_status: 'No status',
};

const currency = (code, currencies) => currencies?.find(currencyObj => currencyObj.code === code);

export type ConditionValue = {
  title: string;
  description: string;
  condition_groups: any; // eslint-disable-line @typescript-eslint/no-explicit-any
};

type RequirementCardContentProps = {
  title: string;
  internalTitle?: string;
  description: string;
  preview?: boolean;
  conditionTitle?: string;
  onToggleConditionModal?: (value: ConditionValue) => any; // eslint-disable-line @typescript-eslint/no-explicit-any
  conditionValues?: ConditionValue;
  requirementFlags?: {
    flagName: IIconType;
    toolTipMsg: string;
  }[];
  showFullfillRequirementButton?: boolean;
  showFlywireButtons?: boolean;
  handleEdit?: React.MouseEventHandler<HTMLSpanElement>;
  onViewLinkedApplications?: React.MouseEventHandler<HTMLSpanElement>;
  status: all_status;
  isInactive: boolean;
  onStatusChange: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  statusReadOnly: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  statusOptions: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  requirement: Requirement;
  programId: number;
  schoolGroupId: number;
  isRefreshingRequirementStatus: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  fulfillmentMessage?: {
    type: 'error' | 'info';
    code: FulfillmentMessageCode;
    mb: number;
  };
  entityPopup?: string;
  userIsAdmin?: boolean;
  userPermissions?: { [key: string]: boolean };
  displayAdditionalAttachments?: boolean;
  displayExternalLinks?: boolean;
  applicationStatus?: string;
  applicationStatusMessage?: string;
  onFiles?: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  onQuestionRequirementClick?: () => void;
  setFulfillmentMessage?: (params: { type: 'info'; code: FulfillmentMessageCode }) => void;
  onToggleQuestionsModal?: () => void;
  onSelectAttachment?: () => void;
  dispatch?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  reloadAggregatedData?: () => void;
  RemoveFileDialog: ComponentType;
  HybridRequirementDecisionReasons: ComponentType;
  HybridRequirementDialogButton: ComponentType;
  aggregatedDataRequirements: Array<{
    id: string;
    decisionReasons: Array<unknown>;
  }>;
};

const RequirementCardContent = ({
  title,
  internalTitle,
  description,
  conditionTitle,
  onToggleConditionModal,
  conditionValues,
  requirementFlags,
  handleEdit,
  preview,
  showFullfillRequirementButton,
  showFlywireButtons,
  onViewLinkedApplications,
  status,
  isInactive,
  onStatusChange,
  statusReadOnly,
  statusOptions,
  requirement,
  programId,
  schoolGroupId,
  isRefreshingRequirementStatus,
  fulfillmentMessage,
  entityPopup,
  userIsAdmin,
  userPermissions,
  displayAdditionalAttachments,
  displayExternalLinks,
  applicationStatus,
  applicationStatusMessage,
  onFiles,
  onQuestionRequirementClick,
  setFulfillmentMessage,
  onToggleQuestionsModal,
  onSelectAttachment,
  dispatch,
  reloadAggregatedData,
  RemoveFileDialog,
  HybridRequirementDecisionReasons,
  HybridRequirementDialogButton,
  aggregatedDataRequirements,
}: RequirementCardContentProps): React.ReactNode => {
  const completed = requirement.status === 'status_completed';
  const paymentRequirement = requirement.requirement_template_type === 'payment';
  const receiptRequirement = requirement.requirement_template_type === 'receipt';
  const currencyCode = requirement?.currency_amount && requirement?.currency_amount.currency;
  const { currencies }: any = useInitialProps(); // eslint-disable-line @typescript-eslint/no-explicit-any
  const originalAmount =
    requirement?.currency_amount &&
    requirement?.currency_amount.amount_cents != null &&
    currencyCode &&
    formatCurrency(
      requirement?.currency_amount.amount_cents,
      currency(currencyCode, currencies),
      true,
      true,
    );
  const finalAmount =
    requirement?.final_price_cents != null &&
    formatCurrency(requirement?.final_price_cents, currency(currencyCode, currencies), true, true);
  const showPromotion =
    finalAmount &&
    originalAmount &&
    requirement?.final_price_cents < requirement.currency_amount?.amount_cents;
  const showAmount = receiptRequirement || paymentRequirement;
  const linkedApplicationsVisible =
    requirement?.once_per_student && requirement?.requirement_template_id != null && userIsAdmin;

  const completedBy: { autoCompleted?: string } = {};

  const isFlywireOptionsVisible = requirement.flywire && showFlywireButtons;

  if (
    requirement?.status === 'status_completed' &&
    requirement?.completed_by === 'ielts_verification_service'
  ) {
    completedBy.autoCompleted = 'Auto-completed using IELTS verification service';
  }
  const {
    role: userRole,
    application,
    id: applicationId,
  }: {
    role: string;
    application: { current_stage: string };
    id: string;
  } = useInitialProps();
  const { trackEvent } = useEventTracker({
    data: {
      userRole,
      requirementCategory: requirement.requirement_category,
      requirementTemplateId: requirement.requirement_template_id,
      requirementType: requirement.requirement_template_type,
      applicationStatus: applicationStatusMessage,
      applicationStage: application.current_stage,
      requirementInstanceId: requirement.id,
      isEarlyAccess: requirement.is_early_access,
    },
  });

  return (
    <Box display="flex" flexWrap="wrap" width="100%">
      <RequirementCardContentWrapper display="flex" flexWrap="nowrap" width="100%">
        <VerticalAlign preview={preview}>
          <RequirementStatus
            id={requirement.id}
            status={status}
            isInactive={isInactive}
            onChange={(newStatus: string) => {
              onStatusChange(newStatus).then(async () => {
                await loadBaseApplication(applicationId, dispatch);
                reloadAggregatedData();
              });
              trackEvent({
                event: `Single Application - Applicant Requirements - Updated Requirement Status To ${
                  statusUpdateEventType[`status_${newStatus}`]
                }`,
                data: {
                  previousRequirementStatus: statusUpdateEventType[requirement.status],
                },
              });
            }}
            noLabel={preview}
            statusReadOnly={statusReadOnly}
            options={statusOptions}
            loading={isRefreshingRequirementStatus}
          />
        </VerticalAlign>
        <RequirementDetails completed={completed}>
          {/* This Chip/Tooltip is intended to be a temporary introduction for users to the new hybrid requirements.
          See: https://applyboard.atlassian.net/browse/APA-678
          If you are looking at this code after Q1 2024, please check with product whether it can be removed. */}
          {requirement.requirement_template_type === 'hybrid' && (
            <Flex pb={2}>
              <CrystalTooltip label="This new requirement type offers a slightly different way to provide requirement responses. Use 'Edit' button on the right to provide answers and upload documents. There is a guided step-by-step process for that. Once all requirement data is submitted, a new 'View' button shows up. It can be used to check all submitted requirement responses.">
                <CrystalChip icon={InfoOutlineIcon} width="hug">
                  New requirement type
                </CrystalChip>
              </CrystalTooltip>
            </Flex>
          )}
          {requirement.early_access_given && (
            <EarlyAccessMessage
              earlyAccessDescription={requirement.early_access_description}
              trackEvent={trackEvent}
              requirementStatus={statusUpdateEventType[requirement.status]}
            />
          )}
          {fulfillmentMessage && (
            <FulfillmentMessage
              type={fulfillmentMessage.type}
              code={fulfillmentMessage.code}
              mb={1}
            />
          )}
          <span className="title-date">
            <span
              className="requirement-title"
              data-tip=""
              data-for={`entity-tooltip-${requirement.id}`}
            >
              <ReactTooltip id={`entity-tooltip-${requirement.id}`}>{entityPopup}</ReactTooltip>
              {title}

              {showAmount && (
                <span>
                  {' ('}
                  <span>
                    {showPromotion && (
                      <PaymentRequirementPromotion>{originalAmount}</PaymentRequirementPromotion>
                    )}
                    {showPromotion ? ` ${finalAmount}` : originalAmount}
                  </span>
                  )
                </span>
              )}
            </span>
            {!preview && (
              <Box display="flex" alignItems="center">
                {handleEdit && (
                  <EditRequirementButton
                    requirement={requirement}
                    handleEdit={handleEdit}
                    completed={completed}
                  />
                )}
                {linkedApplicationsVisible && (
                  <LinkedRequirementsButton
                    onViewLinkedApplications={onViewLinkedApplications}
                    requirement={requirement}
                  />
                )}
                <DueDate
                  userPermissions={userPermissions}
                  requirement={requirement}
                  dispatch={dispatch}
                  userIsAdmin={userIsAdmin}
                  completed={completed}
                  reloadAggregatedData={reloadAggregatedData}
                />
                <RequirementLinkButton applicationId={applicationId} requirement={requirement} />
              </Box>
            )}
          </span>
          {internalTitle && <span className="requirement-internal-title">{internalTitle}</span>}
          {conditionTitle && (
            <RequirementConditionTitle onClick={() => onToggleConditionModal(conditionValues)}>
              {conditionTitle}
            </RequirementConditionTitle>
          )}
          <SeeMore
            expanded={false}
            className="req-desc"
            minimumContentHeight={displayAdditionalAttachments ? 20 : 40}
            onChange={(isExpanded: boolean) => {
              if (isExpanded) {
                trackEvent({
                  event: 'Single Application - Applicant Requirements - Clicked See More',
                  data: {
                    requirementStatus: statusUpdateEventType[requirement.status],
                    isEarlyAccess: requirement.is_early_access,
                  },
                });
              }
            }}
          >
            {requirement.requirement_template_type === 'hybrid' ? (
              <Markdown
                markdown={description}
                headingSettings={{
                  baseLevel: 5,
                  styles: {
                    h1: {
                      variant: 'bodyM',
                      contrast: 'mid',
                      bold: true,
                    },
                  },
                }}
              />
            ) : (
              <RawHtml html={description} />
            )}
          </SeeMore>
          {isFlywireOptionsVisible && <FlywireOptions requirement={requirement} />}
          <div className="links-attachments">
            {displayExternalLinks && !preview && (
              <ExternalLinks links={requirement.application_external_links} />
            )}
            {displayAdditionalAttachments && !preview && (
              <AdditionalAttachments attachments={requirement.application_additional_attachments} />
            )}
          </div>
          <RequirementFlags flags={requirementFlags} flagsAtBottom otherFlags={completedBy} />
          {userIsAdmin && requirement.completed_by === 'road_runner' && (
            <Flex pb={4}>
              <Tag intent="primary" size="lg" icon="fill">
                Road Runner auto-processed this requirement.
              </Tag>
            </Flex>
          )}
        </RequirementDetails>
        {!preview && (
          <>
            <TypeIcons>
              {isInternalRequirement(requirement) && requirement.official && (
                <img
                  src={StudentRecordIcon}
                  alt="StudentRecordIcon"
                  data-tip="This requirement will show up on Student Records"
                  data-for={`tooltip-${requirement.id}`}
                />
              )}
              {isInternalRequirement(requirement) &&
                requirement.applyproof_compatible &&
                requirement.requirement_template_type !== 'disclaimer' && (
                  <img
                    src={ApplyproofIcon}
                    alt="ApplyProofIcon"
                    data-tip="This requirement is ApplyProof Compatible"
                    data-for={`tooltip-${requirement.id}`}
                  />
                )}
            </TypeIcons>
            {showFullfillRequirementButton && (
              <FulfillRequirementButton
                walkme-id="requirements-uploadbutton"
                applicationStatus={applicationStatus}
                applicationId={applicationId}
                onFiles={event => {
                  onFiles(event).then(async () => {
                    trackEvent({
                      event: 'Single Application - Application Requirements - Fulfilled',
                      experiment_event_key:
                        'Single Application - Application Requirements - Fulfilled',
                      data: {
                        requirementClassification: requirement.classification,
                        requiredState: requirement.required_state,
                      },
                    });
                    await loadBaseApplication(applicationId, dispatch);
                    reloadAggregatedData();
                  });
                }}
                onPaymentSuccess={() => {
                  trackEvent({
                    event: 'Single Application - Application Requirements - Fulfilled',
                    experiment_event_key:
                      'Single Application - Application Requirements - Fulfilled',
                    data: {
                      requirementClassification: requirement.classification,
                      requiredState: requirement.required_state,
                    },
                  });
                }}
                userPermissions={userPermissions}
                requirement={requirement}
                onQuestionRequirementClick={onQuestionRequirementClick}
                userIsAdmin={userIsAdmin}
                fulfillmentMessage={fulfillmentMessage}
                onMessage={setFulfillmentMessage}
                isRefreshingRequirementStatus={isRefreshingRequirementStatus}
                reloadAggregatedData={reloadAggregatedData}
                HybridRequirementDialogButton={HybridRequirementDialogButton}
              />
            )}
          </>
        )}
      </RequirementCardContentWrapper>
      {requirement.requirement_template_type !== 'disclaimer' && (
        <RequirementCardBottomBar
          requirement={requirement}
          preview={preview}
          onToggleQuestionsModal={onToggleQuestionsModal}
          onClickOverride={preview ? onSelectAttachment : null}
          reloadAggregatedData={reloadAggregatedData}
          programId={programId}
          schoolGroupId={schoolGroupId}
          RemoveFileDialog={RemoveFileDialog}
          HybridRequirementDecisionReasons={HybridRequirementDecisionReasons}
          aggregatedDataRequirements={aggregatedDataRequirements}
        />
      )}
    </Box>
  );
};

const FulfillmentMessage = ({ type, code, mb }: FulfillmentMessageProps) => {
  const tooltipId = useId();
  const chipColor = type === 'error' ? 'negative' : 'info';
  const iconColor = type === 'error' ? 'negative' : 'info';
  const chipLabel = strings.fulfillmentSummaryMessages[code];
  const chipTooltip = strings.fulfillmentDetailedMessages[code];

  return (
    <Box mb={mb} data-tip={chipTooltip} data-for={tooltipId}>
      <Chip
        icon={<InfoOutlineIcon color={iconColor} />}
        color={chipColor}
        label={chipLabel}
        allowMultiline
      />
      <FulfillmentTooltip id={tooltipId} />
    </Box>
  );
};

const EarlyAccessMessage = ({
  earlyAccessDescription,
  requirementStatus,
  trackEvent,
}: {
  earlyAccessDescription: string;
  requirementStatus: string;
  trackEvent: (event: Pick<IEvent, 'event' | 'error' | 'action' | 'group' | 'data'>) => void;
}) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const openRef = useRef(tooltipOpen);
  const tooltipId = useId();
  openRef.current = tooltipOpen;

  const trackHoverAfterDelay = () => {
    setTimeout(() => {
      if (openRef.current) {
        trackEvent({
          event: 'Single Application - Applicant Requirements - Hovered Early Access Chip',
          data: {
            requirementStatus,
          },
        });
      }
    }, 1000);
  };

  return (
    // Using 'mouseEnter' on the parent Box component because there is an issue with this version of the MUI tooltip
    // The existing `onOpen` and `onClose` callbacks can be invoked repeatedly as the cursor moves over child elements
    // See: https://github.com/mui/material-ui/pull/23482/files
    <Box mb={1} onMouseEnter={trackHoverAfterDelay} onFocus={trackHoverAfterDelay}>
      <Tooltip
        id={tooltipId}
        onOpen={() => setTooltipOpen(true)}
        onClose={() => setTooltipOpen(false)}
        title={earlyAccessDescription}
      >
        <Chip
          color="magic"
          tabIndex={0}
          aria-describedby={tooltipId}
          icon={<InfoOutlineIcon color="magic.dark" />}
        >
          Required at a later stage
        </Chip>
      </Tooltip>
    </Box>
  );
};

const RequirementLinkButton = ({
  applicationId,
  requirement,
}: {
  applicationId: string;
  requirement: Requirement;
}) => {
  const label = 'Click to copy link to requirement.';
  const { trackEvent } = useEventTracker();
  const tabName =
    requirement.classification === 'applicant_requirement' ? 'applicant-requirements' : 'actions';
  return (
    <Tooltip title={label} placement="top">
      <IconButton
        ml={1}
        size="small"
        aria-label={label}
        onClick={() => {
          trackEvent({
            event: 'Single Application - Requirements - Clicked on Requirement Link',
            data: {
              requirementCategory: requirement.requirement_category,
              requirementTemplateId: requirement.requirement_template_id,
              requirementType: requirement.requirement_template_type,
              requirementInstanceId: requirement.id,
              isEarlyAccess: requirement.is_early_access,
              requirementStage: requirement.application_stage,
              requirementClassification: requirement.classification,
              requirementStatus: requirement.status,
            },
          });
          copyToClipboard(
            `${window.location.origin}/applications/${applicationId}?tab=${tabName}#req-${requirement.id}`,
          ).then(isSuccess => {
            if (isSuccess) {
              raiseInfoMessage('Link copied to clipboard.');
            }
          });
        }}
      >
        <LinkOutlineIcon color="primary" />
      </IconButton>
    </Tooltip>
  );
};

const EditRequirementButton = ({
  handleEdit,
  completed,
  requirement,
}: {
  handleEdit: React.MouseEventHandler<HTMLSpanElement>;
  completed: boolean;
  requirement: Requirement;
}) => {
  const label = !(
    completed ||
    requirement.status === 'status_pending_receipt' ||
    requirement.status === 'status_pending_verification'
  )
    ? 'Click to edit requirement'
    : 'The requirement cannot be edited because it has been completed';

  return (
    <Tooltip placement="top" title={label}>
      <IconButton size="small" ml={1} onClick={handleEdit} aria-label={label}>
        <EditOutlineIcon color="primary" />
      </IconButton>
    </Tooltip>
  );
};

const LinkedRequirementsButton = ({
  onViewLinkedApplications,
}: {
  onViewLinkedApplications: React.MouseEventHandler<HTMLSpanElement>;
}) => {
  const label = 'Click to view linked applications';

  return (
    <Tooltip title={label} placement="top">
      <IconButton ml={1} aria-label={label} onClick={onViewLinkedApplications} size="small">
        <FormatListBulletedOutlineIcon color="primary" />
      </IconButton>
    </Tooltip>
  );
};

export default RequirementCardContent;
